import { DataSecurityMgr } from '../DataSecurityMgr';
/**
 * @class GBDManager
 * Class to help expose shareable data (cview3 cookie) for the miniapps to read using the GetBrowserData library
 * The mini-app will be able to use this browser data generated and include this information in the Headers of the API call to the downstream systems.
 *
 * @example
 * To set this up in a shell you'll need to know the Library URL,
 * So that when the AppLoader is loading and setting up the mini-app it is able to create the class and set the script tag up on the dom.
 *
 * **NOTE** The Shell only needs to follow the example below, as this initialisation happens within the AppLoader By default
 *
 * ```typescript
 * const gbd = new GBDManager("URL TO THE LIBRARY");
 * // this return the Get browser data string now.
 * const gbd: string = await gbd.getData();
 * ```
 *
 * @example
 * Shell setup for data Security libraries, usage inside the AppLoader.load
 * To enable this Library in your application.
 *
 * ```typescript
 * AppLoader.load({
 *   appMetadata: {
 *     ...
 *     properties: {
 *       ...
 *       dataSecurity: {
 *         ...
 *         gbd: {
 *           LibraryUrl: 'https://www.test.com/libraryUrlForGBD'
 *         }
 *       }
 *     }
 *   }
 * });
 * ```
 * @example
 * Mini-app(s) can use this objects passed to them as `dataSecurity` property to App.tsx
 * Irrespective of whether the library is passed or not, the class for GBD will be passed down as `dataSecurity.gbd`
 *
 * Mini-app(s) can still use `await dataSecurity.gbd.getData()`, to see if the promise resolution is positive or failure and handle their case accordingly
 *
 * ```typescript
 * const App = function({ ..., dataSecurity}) {
 *   ...
 *   let data = '';
 *   try{
 *      data  = await dataSecurity.gbd.getData();                        // returns the GBD cookie value
 *   }
 *   catch(e) {
 *      warn('unable to get the data', e);
 *   }
 *
 *  // using the data.
 *   fetch({
 *     url: 'https://downstream.system.com/server/to/call',
 *     headers: {
 *       ...
 *       ...(data && { 'x-nab-custom-header-for-gbd': data })
 *     }
 *   })
 *   ...
 * }
 * ```
 */
class GBDManager extends DataSecurityMgr {
    /**
     * Used to set the instantiated class with configurable values.
     * @param cdnUrl Library URI to be loaded. Usually a CDN path.
     * @param browserDataMethodName The method name to retrieve the browser data has been known to be changed in some implementations,
     * this allow a custom function name to be used in place of the default function name (getBrowserData).
     */
    constructor(cdnUrl, browserDataMethodName) {
        super(cdnUrl, browserDataMethodName);
        this.COOKIE_NAME = 'cview3';
        this.DEFAULT_BROWSER_DATA_METHOD_NAME = 'getBrowserData';
        this.initialise();
    }
    /**
     * Required to be initialized on load of the application.
     * This will load the GBD library using the specified cdnURL.
     */
    async initialise() {
        return this.loadLibrary();
    }
    /**
     * Used to check if a cookie that matches the COOKIE_NAME is present in window.document.cookie
     * If the cookie value has not been set, then initialization is set to be imcomplete
     */
    hasCookieData() {
        const cookieMatch = window.document.cookie.match(new RegExp(`(^| )${this.COOKIE_NAME}=([^;]+)`));
        if (cookieMatch && cookieMatch.length > 2) {
            return !!cookieMatch[2];
        }
        return false;
    }
    /**
     * The data generated by the GBD library can be retrieved here.
     * Errors will be thrown if the global objects are not available from the library or required data is not set by the library
     */
    async getData() {
        // Uses the default method name if a custom browser data method name is not provided
        const methodName = this.config || this.DEFAULT_BROWSER_DATA_METHOD_NAME;
        // Calls the browser data method iff the method name is present in the window and cookie has been initialized
        if (window[methodName] && this.hasCookieData()) {
            const browserData = window[methodName]();
            if (browserData) {
                return browserData;
            }
            return Promise.reject(new Error(`Failed reading browser data.`));
        }
        return Promise.reject(new Error('Data security feature for browser data not initialised.'));
    }
}
export { GBDManager };
//# sourceMappingURL=GBDManager.js.map